import { Rule, Operation, Selector } from "./types"
import { EltType, Attr, AttrList } from "../pandoc"
import { OutDesignAST, OutDesignNode, emptyAttr } from "../ast"
import { isMatchingSelectorList, parseSelectorList } from "./selectors"
import { walkAST } from "./walk";
import { containsBlocks, containsInlines, isBlockType, isContentOneLineBreak,
  stringifyNode, textToInlines, blockToInlines, mergeConsecutiveStr } from "./utils";

export type OutDesignOperation = (node: OutDesignNode) => OutDesignNode | OutDesignNode[] | undefined;

type Context = {
  idCounters: Record<string, number>
}

export function applyRules(ast: OutDesignAST, rules: Rule[]): OutDesignAST {
  const copy = structuredClone(ast)
  const context: Context = { idCounters: {} }

  const action: OutDesignOperation = (node) => {
    for (const rule of rules) {
      if (!rule.selector) continue

      const matched = isMatchingSelectorList(node, parseSelectorList(rule.selector))
      if (!matched) continue

      node = applyRule(node, rule, context)
    }
    return node
  }

  let newAst: OutDesignAST = walkAST(copy, action)

  // Replicating the Lua execution order...
  // We will eventually have to find a better solution.
  newAst.blocks = groupAndJoin(newAst.blocks, rules)

  return {
    blocks: newAst.blocks,
    "pandoc-api-version": newAst["pandoc-api-version"],
    meta: newAst.meta
  }
}

function applyRule(node: OutDesignNode, rule: Rule, context: Context) {
  const o: Operation = rule.operation;
  if (!(Object.keys(o).length)) return node;
  if (o.delete) return [];
  if (!(o.empty)) {
    if(isContentOneLineBreak(node)) {
      return [];
    }
  }
  if (o.level) {
    node = applyLevel(node, o.level)
  }
  if (o.type) {
    node = applyType(node, o.type as EltType) as OutDesignNode
  }
  if (o.unwrap) {
    node = applyUnwrap(node)
  }
  if (o.classes === "" || o.classes || typeof o.classes === "boolean") {
    // careful, this logic is wrong as it only works for simple selectors
    // and not multiple ones.
    const selectorClasses = parseSelectorList(rule.selector)[0].classes
    // Also, the logics here is weak as the node can mutate into
    // several ones... opening for bugs in the rest of the manipulations
    node = applyClasses(node, o.classes, selectorClasses as Array<string>)
  }
  if (o.id) {
    node = applyId(node, context.idCounters)
  }
  if (o.attrs) {
    node = applyAttr(node, o.attrs)
  }
  if (o.wrap) {
    node = applyWrap(node, o.wrap)
  }
  if (o.simplify) {
    node = simplify(node)
  }
  if (o.br) {
    node = insertLineBreakBefore(node)
  }
  return node
}

/**
 * All the operations are listed here!
 */

function applyType(node: OutDesignNode, type: EltType): OutDesignNode {
  if(containsInlines(node.type) && containsBlocks(type)) {
    // Case with an inline to a block
    return {
      type: type,
      attr: node.attr,
      children: [{ type: "Para", children: node.children}]
    }
  } else if(containsInlines(type) && containsBlocks(node.type)) {
    // Case with a block to an inline
    const first = node.children?.[0]

    if (!first || !first.children) {
      return node // or throw
    }

    return {
      type,
      attr: node.attr,
      children: first.children
    }
  } else if ( node.type === "Code") {
    node.type = type
    node.children = textToInlines(node.data?.text as string)
    // with this, it won't work if we wanna turn Code into a Block for example
  } else {
    node.type = type;
    if(!node.data) node.data = {}
    node.data.text = stringifyNode(node);
  }
  return node
}

function applyLevel(node: OutDesignNode, level: number): OutDesignNode {
  if (node.data === undefined) {
    node.data = { level: parseInt(level as any) };
  } else {
    node.data.level = parseInt(level as any);
  }
  return node;
}

function applyClasses(node: OutDesignNode, classes: string | boolean, selectorClasses: Array<String>): OutDesignNode | OutDesignNode[] {
  let found = false
  if(typeof classes === "boolean" && !classes) {
    if (node.attr) {
      node.attr[1] = []
    }
  } else {
    if(typeof classes === "string") {
      let classesArray: Array<string> = classes === "" ? [] : classes.split(" ")
      if (!node.attr) {
        node.attr = ["", classesArray, []]
      } else {
        if(classesArray.length === 0) {
          classesArray = node.attr[1].filter(item => selectorClasses.indexOf(item) < 0);
        }
        node.attr[1] = classesArray
      }
    }
  }

  // applyUnwrap ONLY if it's a wrapper AND now empty
  if (
    (node.type === "Div" || node.type === "Span") &&
    isEmptyAttr(node.attr)
  ) {
    return applyUnwrap(node) 
  }

  return node
}

function applyId(node: OutDesignNode, idCounters: Record<string, number>): OutDesignNode {
  let id: string = ""
  let classes: Array<string> = []
  if(node.attr) {
    id = node.attr[0]
    classes = node.attr[1]
  } else {
    node.attr = ["", [], []]
  }
  
  if (id !== "") return node

  const baseParts: string[] = []

  baseParts.push(String(node.type).toLowerCase())

  if (classes && classes.length > 0) {
    baseParts.push(...classes)
  }

  const baseId = baseParts.join("-")

  // if(idCounters[baseId] > 0) {
  //   idCounters[baseId] = idCounters[baseId] + 1
  // } else idCounters[baseId] = 1
  idCounters[baseId] = (idCounters[baseId] || 0) + 1
  const counter = idCounters[baseId]

  node.attr[0] = `${baseId}-${counter}`

  return node
}

function applyWrap(node: OutDesignNode, wrapper: string): OutDesignNode {
  if (node.data?._generated) return node;
  const wrapperNode = parseSelectorList(wrapper)[0]
  if(!node.data) node.data = {}
  node.data._generated = true;
  let type : EltType | undefined = wrapperNode.type ;
  if(!type) {
    type = isBlockType(node.type) ? "Div" : "Span"
  }
  return {
    type: type,
    attr: [wrapperNode.id ?? "", wrapperNode.classes ?? [], []],
    children: Array.isArray(node) ? node : [node], // flatten
  }
}

function applyUnwrap(node: OutDesignNode): OutDesignNode | OutDesignNode[] {
  if(node.type === "Para") {
    return simplify(node)
  }
  if(node.type === "Code") {
    return textToInlines(node.data?.text as string) ;
  }
  if(containsBlocks(node.type)) {
    return node.children as OutDesignNode[];
  }
  if(containsInlines(node.type)) {
    return node.children as OutDesignNode[];
  }
  if(!node.data) node.data = {}
  node.data._generated = true;
  return node;
}

function simplify(node: OutDesignNode): OutDesignNode {
  node.attr = emptyAttr()
  return node
}

function insertLineBreakBefore(node: OutDesignNode): OutDesignNode[] {
  if(isBlockType(node.type)) {
    return [{
      type: "Plain",
      children: [{ type: "LineBreak" }]
    }, node]
  } else {
    return [{
      type: "LineBreak",
    }, node]
  }
}

function isEmptyAttr(attr?: Attr): boolean {
  if (!attr) return true
  const [id, classes, kv] = attr
  return !id && classes.length === 0 && kv.length === 0
}

function applyAttr(node: OutDesignNode, kvs: AttrList): OutDesignNode {
  kvs = normalizeAttrList(kvs)
  if (node.attr === undefined) {
    node.attr = ["", [], kvs]
  } else {
    node.attr[2] = mergeAttrList(node.attr[2], kvs);
  }
  return node;
}

function normalizeAttrList(input: unknown): AttrList {
  if (!input) return []

  if (Array.isArray(input) && Array.isArray(input[0])) {
    return input as AttrList
  }

  if (
    Array.isArray(input) &&
    input.length === 2 &&
    typeof input[0] === "string"
  ) {
    return [input as [string, string]]
  }

  if (typeof input === "object") {
    return Object.entries(input as Record<string, string>)
  }

  return []
}

// function applyUnwrap(node: OutDesignNode): OutDesignNode | OutDesignNode[] {
//   if (!node.children) return node
//   return node.children
// }

function mergeAttrList(base: AttrList, toMerge: AttrList): AttrList {
  if (!Array.isArray(base)) base = []
  if (!Array.isArray(toMerge)) toMerge = []

  const tempMap = new Map<string, [string, string]>();

  [...base, ...toMerge].forEach(attr => {
    tempMap.set(attr[0], attr);
  });

  return Array.from(tempMap.values()) as AttrList;
}

export function rolesToClasses(ast: OutDesignAST): OutDesignAST {
  const action = (node: OutDesignNode) => {
    if (node.attr) applyRole(node.attr)
    return node
  }

  const newAst: OutDesignAST = walkAST(ast, action)

  return {
    blocks: newAst.blocks,
    "pandoc-api-version": newAst["pandoc-api-version"],
    meta: newAst.meta
  }
}

function applyRole(attr: Attr) {
  const classes = attr[1];
  const attrs = attr[2];

  let i = 0;

  while (i < attrs.length) {
    const [key, value] = attrs[i];

    if (key === "role") {
      for (const role of value.split(/\s+/)) {
        if (!classes.includes(role)) {
          classes.push(role);
        }
      }

      attrs.splice(i, 1);
      continue;
    }

    i++;
  }
}

function groupAndJoin(blocks: OutDesignNode[], rules: Rule[]): OutDesignNode[] {
  for (const rule of rules) {
    const op = rule.operation
    if (!op.group && !op.join) continue

    const selectors = parseSelectorList(rule.selector)

    let result: OutDesignNode[] = []
    let i = 0

    while (i < blocks.length) {
      const matched = isMatchingSelectorList(blocks[i], selectors)

      if (!matched) {
        result.push(blocks[i])
        i++
        continue
      }

      const chunk: OutDesignNode[] = []

      while (i < blocks.length && isMatchingSelectorList(blocks[i], selectors)) {
        chunk.push(blocks[i])
        i++
      }

      if (op.group) {
        let selectorString: string
        let parsed: Selector = {}

        if (typeof op.group === "string") {
          selectorString = op.group
          parsed = parseSelectorList(selectorString)[0]
        } else {
          if(op.group.type) parsed.type = op.group.type
          if(op.group.classes) parsed.classes = op.group.classes.split(" ")
        }

        const wrapperTag = parsed.type || "Div"
        const wrapperClasses = parsed.classes || []

        const isListGroup =
          wrapperTag === "BulletList" || wrapperTag === "OrderedList"

        const groupedItems: any[] = []

        for (const blk of chunk) {
          if (isListGroup) {
            if (blk.type === "Div" && blk.children?.length) {
              groupedItems.push(blk.children)
            } else {
              groupedItems.push([simplify(blk)])
            }
          } else {
            groupedItems.push(simplify(blk))
          }
        }

        let groupedEl: OutDesignNode

        if (isListGroup) {
          groupedEl = {
            type: wrapperTag,
            children: groupedItems
          }

          if (wrapperClasses.length > 0) {
            groupedEl = {
              type: "Div",
              attr: ["", wrapperClasses, [["wrapper", "1"]]],
              children: [groupedEl]
            }
          }
        } else if(wrapperTag == "LineBlock") {
            groupedEl = {
              type: "LineBlock",
              children: groupedItems.map(blockToInlines)
            }
        }else {
          groupedEl = {
            type: wrapperTag,
            attr: ["", wrapperClasses, []],
            children: groupedItems
          }
        }

        result.push(groupedEl)
        continue
      }

      if (op.join) {
        const joinConfig = parseJoin(op.join)
        const sepNode = makeSeparator(joinConfig.separator)

        const blockType = chunk[0].type

        let joined: OutDesignNode

        if (containsBlocks(blockType)) {
          let content: OutDesignNode[] = []

          chunk.forEach((node, idx) => {
            if (idx > 0) {
              content.push(sepNode)
            }
            
            content.push(...(blockToInlines(node) ?? []))
          })

          content = mergeConsecutiveStr(content)

          joined = {
            type: blockType,
            attr: ["", joinConfig.classes, []],
            children: [{ type: "Para", children: content }]
          }

        } else {
          const content: OutDesignNode[] = []

          chunk.forEach((node, idx) => {
            const inlines = extractInlines(node)

            if (idx > 0) content.push(sepNode)

            content.push(...inlines)
          })

          if (joinConfig.type === "Header") {
            joined = {
              type: "Header",
              data: { level: joinConfig.level ?? 1 },
              attr: ["", joinConfig.classes, []],
              children: mergeConsecutiveStr(content)
            }
          } else {
            joined = {
              type: chunk[0].type,
              attr: ["", joinConfig.classes, []],
              children: mergeConsecutiveStr(content)
            }
          }
        }
        result.push(joined)
        continue
      }
    }

    blocks = result
  }

  return blocks
}

function parseJoin(op: any) {
  if (typeof op === "string") {
    const [sep, cls] = op.split(".")

    return {
      separator: sep,
      classes: cls ? [cls] : [],
      type: null,
      level: null
    }
  }

  return {
    separator: op.separator ?? "Space",
    classes: op.classes ? [op.classes] : [],
    type: op.type ?? null,
    level: op.level ?? null
  }
}

function makeSeparator(sep: string): OutDesignNode {
  if (sep === "LineBreak") return { type: "LineBreak" }
  if (sep === "Space") return { type: "Space" }

  return {
    type: "Str",
    data: { text: sep }
  }
}

function extractInlines(node: OutDesignNode): OutDesignNode[] {
  if (containsInlines(node.type)) {
    return node.children as OutDesignNode[] ?? []
  }

  if (containsBlocks(node.type)) {
    return extractInlines(node.children[0]) ?? []
  }

  return []
}